import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DateTimeFormat, Spinner, Tabs, Text, useLocalizeMessage, } from 'libs.nucleus';
import { PageHeader, PageWrapper, useBooleanState } from 'libs.react';
import { DcrCancelModal } from '../../components/dcr_cancel_modal';
import { DcrComments } from '../../components/dcr_comments';
import { DcrExecuteModal } from '../../components/dcr_execute_modal';
import { DcrFormModal } from '../../components/dcr_form_modal';
import { DcrStatusBadge } from '../../components/dcr_status_badge';
import { DcrStatusModal } from '../../components/dcr_status_modal';
import { DcrSummary } from '../../components/dcr_summary';
import { useDataChangeRequestsContext, useDcrApiContext } from '../../contexts';
import { useDcrDetail } from '../../hooks';
import { canCancelDcr, canChangeDcrStatus, canEditDcr, getDcrExecuteBlockedReason, getDcrTypeLabel } from '../../utils';
const Section = ({ label, children }) => (_jsxs("div", { className: 'flex flex-col gap-4 border-t border-neutral-200 py-6 md:flex-row md:gap-8', children: [_jsx(Text, { size: 'sm', className: 'font-semibold md:w-48 md:shrink-0', children: label }), _jsx("div", { className: 'flex flex-1 flex-col gap-4', children: children })] }));
export const DcrDetailView = ({ breadcrumbPages = [] } = {}) => {
    const translate = useLocalizeMessage();
    const { dcrId } = useParams();
    const apiContext = useDcrApiContext();
    const { basePath, canEdit, canComment, canProcess } = useDataChangeRequestsContext();
    const [isEditOpen, openEdit, closeEdit] = useBooleanState();
    const [isCancelOpen, openCancel, closeCancel] = useBooleanState();
    const [isStatusOpen, openStatus, closeStatus] = useBooleanState();
    const [isExecuteOpen, openExecute, closeExecute] = useBooleanState();
    const { data: record, isLoading } = useDcrDetail(apiContext, dcrId);
    const trail = useMemo(() => [
        ...breadcrumbPages,
        { name: translate('Data changes'), path: basePath },
        // Last crumb is the current page: rendered as plain text, never a link
        ...(record ? [{ name: record.dcr_intake__number, path: '' }] : []),
    ], [breadcrumbPages, basePath, record, translate]);
    const headerOptions = [];
    if (canEdit && record) {
        headerOptions.push({
            name: translate('Edit'),
            onClick: openEdit,
            variant: 'primary',
            disabled: !canEditDcr(record.dcr_intake__status),
        });
        if (canCancelDcr(record.dcr_intake__status)) {
            headerOptions.push({ name: translate('Cancel'), onClick: openCancel, variant: 'secondary' });
        }
    }
    // Both stay on the page whatever state the request is in, so it is always clear what a study
    // manager can do with a request and what they could have done with it
    if (canProcess && record) {
        const executeBlockedReason = getDcrExecuteBlockedReason(record);
        headerOptions.push({
            name: translate('Change status'),
            onClick: openStatus,
            variant: 'secondary',
            disabled: !canChangeDcrStatus(record.dcr_intake__status),
            tooltip: canChangeDcrStatus(record.dcr_intake__status)
                ? undefined
                : translate('This request is closed and its status can no longer change.'),
            dataTestId: 'dcr-change-status-action',
        });
        headerOptions.push({
            name: translate('Execute'),
            onClick: openExecute,
            variant: 'primary',
            disabled: !!executeBlockedReason,
            tooltip: executeBlockedReason ? translate(executeBlockedReason) : undefined,
            dataTestId: 'dcr-execute-action',
        });
    }
    if (isLoading) {
        return (_jsx(PageWrapper, { children: _jsx(Spinner, { wrapper: 'full' }) }));
    }
    if (!record) {
        return (_jsx(PageWrapper, { children: _jsx(PageHeader, { title: translate('Data change request not found'), LinkComponent: Link }) }));
    }
    const tabItems = [
        {
            label: translate('Details'),
            content: (_jsxs("div", { className: 'flex flex-col', children: [_jsxs(Section, { label: translate('Summary'), children: [_jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Text, { size: 'sm', className: 'font-medium', children: translate('Status') }), _jsx(DcrStatusBadge, { status: record.dcr_intake__status })] }), _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Text, { size: 'sm', className: 'font-medium', children: translate('Action') }), _jsx(Text, { size: 'sm', className: 'text-neutral-700', children: record.dcr_intake__action || '—' })] }), record.dcr_intake__note ? (_jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Text, { size: 'sm', className: 'font-medium', children: translate('Note') }), _jsx(Text, { size: 'sm', className: 'text-neutral-700', children: record.dcr_intake__note })] })) : null, _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Text, { size: 'sm', className: 'font-medium', children: translate('Created by') }), _jsx(Text, { size: 'sm', className: 'text-neutral-700', children: record.dcr_intake__created_by_name || record.dcr_intake__created_by_email || '—' })] }), _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Text, { size: 'sm', className: 'font-medium', children: translate('Creation date') }), _jsx(Text, { size: 'sm', className: 'text-neutral-700', children: _jsx(DateTimeFormat, { date: record.dcr_intake__created_date }) })] })] }), _jsx(DcrSummary, { participantNumber: record.dcr_intake__public_user_number, type: record.dcr_intake__type, changes: record.dcr_intake__changes ?? {}, reason: record.dcr_intake__reason, reasonNotes: record.dcr_intake__reason_notes })] })),
        },
        {
            label: translate('Comments'),
            content: _jsx(DcrComments, { dcrId: record._id, canComment: canComment }),
        },
    ];
    return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { title: record.dcr_intake__number, subtitle: getDcrTypeLabel(record.dcr_intake__type), breadcrumbPages: trail, options: headerOptions, LinkComponent: Link }), isEditOpen ? _jsx(DcrFormModal, { record: record, onClose: closeEdit }) : null, isCancelOpen ? _jsx(DcrCancelModal, { record: record, onClose: closeCancel }) : null, isStatusOpen ? _jsx(DcrStatusModal, { record: record, onClose: closeStatus }) : null, isExecuteOpen ? _jsx(DcrExecuteModal, { record: record, onClose: closeExecute }) : null, _jsx(Tabs, { tabItems: tabItems })] }));
};
